import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useServerSearch } from '../../hooks/useServerSearch';
import { customerLabel } from '../../utils/customer';

/**
 * Reusable customer typeahead (extracted from the maintenance/invoice dialogs).
 * Server-side search via /api/customers/?search=&typeahead=true (org-scoped by
 * the axios interceptor). Emits the selected customer object (or null) via onChange.
 */
function CustomerAutocomplete({
  value,
  onChange,
  label,
  placeholder,
  size = 'small',
  autoFocus = false,
  disabled = false,
  error = false,
  helperText,
}) {
  const { items: customers, autocompleteProps } = useServerSearch('/api/customers/', { minSearchLength: 2 });

  const getOptionLabel = (option) => {
    if (!option) return '';
    const extra = [option.business_id, option.email, option.phone].filter(Boolean)[0];
    if (!option.name) return customerLabel(option);
    return extra ? `${option.name} (${extra})` : option.name;
  };

  return (
    <Autocomplete
      options={customers}
      value={value || null}
      onChange={(e, v) => onChange(v)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      disabled={disabled}
      {...autocompleteProps}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.type === 'company' && (
            <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          )}
          <Box>
            <Typography variant="body2">{customerLabel(option)}</Typography>
            {(option.business_id || option.email) && (
              <Typography variant="caption" color="text.secondary">
                {[option.business_id, option.email].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          autoFocus={autoFocus}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}

export default CustomerAutocomplete;
